import type { FulfillmentStatus, ShipmentStatus } from "@nexxt/contracts/logistics";
import type { SyncStatus } from "@nexxt/contracts/til";
import { m } from "../../../paraglide/messages.js";

// A shipment of a sales order in the app's words (pure, with its tests): its status, the status of what left, and
// who it waits on.

/** The statuses a shipment of the F1 takes (ADR-126, ADR-130): the list filters on these. */
export const f1FulfillmentStatuses = [
  "ready",
  "released_to_3pl",
  "acknowledged",
  "shipped",
  "cancelled",
] as const satisfies readonly FulfillmentStatus[];

export const fulfillmentStatusLabel: Record<FulfillmentStatus, () => string> = {
  draft: m.fulfillment_status_draft,
  ready: m.fulfillment_status_ready,
  document_pending: m.fulfillment_status_document_pending,
  released_to_3pl: m.fulfillment_status_released_to_3pl,
  acknowledged: m.fulfillment_status_acknowledged,
  shipped: m.fulfillment_status_shipped,
  cancelled: m.fulfillment_status_cancelled,
};

export const shipmentStatusLabel: Record<ShipmentStatus, () => string> = {
  planned: m.shipment_status_planned,
  booked: m.shipment_status_booked,
  in_transit: m.shipment_status_in_transit,
  delivered: m.shipment_status_delivered,
  exception: m.shipment_status_exception,
  cancelled: m.shipment_status_cancelled,
};

interface WaitingFulfillment {
  status: FulfillmentStatus;
  syncStatus: SyncStatus;
  createdAt: string;
  shippingInstructionSentAt: string | null;
  acknowledgedAt: string | null;
  cancelRequestedAt: string | null;
  shippedAt: string | null;
  toBook: boolean;
}

type Waiting = "document" | "cancellation" | "resend" | "acknowledgement" | "booking" | "shipment";

/**
 * Who a shipment waits on, and since when: the document Business Central registers before the release (FR-M13-05);
 * the warehouse for a cancellation; Logistics for an instruction refused; the warehouse to take it (FR-M13-06); the
 * Customer Service to book what Tomorrow ships (FR-M13-02, `toBook` from the domain); the warehouse to ship it.
 * Nothing once shipped and booked, or cancelled.
 */
export function fulfillmentWaiting(f: WaitingFulfillment): { on: Waiting; since: string | null } | null {
  if (f.status === "ready") return { on: "document", since: f.createdAt };
  if (f.status !== "released_to_3pl" && f.status !== "acknowledged" && f.status !== "shipped") return null;
  if (f.cancelRequestedAt !== null) return { on: "cancellation", since: f.cancelRequestedAt };
  if (f.syncStatus === "sync_failed") return { on: "resend", since: f.shippingInstructionSentAt };
  if (f.status === "released_to_3pl") return { on: "acknowledgement", since: f.shippingInstructionSentAt };
  if (f.toBook) return { on: "booking", since: f.shippingInstructionSentAt };
  return f.status === "acknowledged" ? { on: "shipment", since: f.acknowledgedAt } : null;
}

const waitingTexts: Record<Waiting, (p: { since: string }) => string> = {
  document: m.fulfillment_waiting_on_document,
  cancellation: m.fulfillment_waiting_on_cancellation,
  resend: m.fulfillment_waiting_on_resend,
  acknowledgement: m.fulfillment_waiting_on_acknowledgement,
  booking: m.fulfillment_waiting_on_booking,
  shipment: m.fulfillment_waiting_on_shipment,
};

/** Who the shipment waits on, in words; `age` says how long ago a moment was. */
export function fulfillmentWaitingOn(f: WaitingFulfillment, age: (at: string) => string): string | null {
  const waiting = fulfillmentWaiting(f);
  if (!waiting) return null;
  return waitingTexts[waiting.on]({ since: waiting.since === null ? "—" : age(waiting.since) });
}

/** The units that left of the units to ship: `3 of 6`. */
export const shippedText = (f: { shippedUnits: number; totalUnits: number }): string =>
  m.fulfillment_units_shipped({ shipped: f.shippedUnits, total: f.totalUnits });

const bookingSyncTexts: Record<SyncStatus, () => string> = {
  not_synced: m.booking_sync_not_synced,
  sync_in_progress: m.booking_sync_in_progress,
  sync_failed: m.booking_sync_failed,
  sync_complete: m.booking_sync_complete,
};

/** How the request of a booking stands towards the TLG shipping platform (ADR-231), and whether it is withdrawn. */
export const bookingSyncLabel = (b: { syncStatus: SyncStatus; cancelRequested: boolean }): string =>
  b.cancelRequested
    ? m.booking_sync_cancel_requested({ state: bookingSyncTexts[b.syncStatus]() })
    : bookingSyncTexts[b.syncStatus]();

/** The order a shipment is of, with its lot when the order ships in lots (ADR-195): `SO-0000013 · lot 2`. */
export const orderOfFulfillment = (f: { salesOrder: { soNumber: string }; lotNo: number | null }): string =>
  f.lotNo === null ? f.salesOrder.soNumber : m.fulfillment_order_lot({ order: f.salesOrder.soNumber, lot: f.lotNo });
